import {ref} from 'vue'
import {useTyro} from './useTyro'
import {usePrinter} from './usePrinter'
import {useTyroStore} from '~/stores/tyro'
import {redactSensitive} from '~/utils/sanitize.js'

// Payment provider types
export const PAYMENT_PROVIDERS = {
    STRIPE_TERMINAL: 'stripe_terminal',
    TYRO: 'tyro',
    TYRO_KIOSK: 'tyro_kiosk',
    WINDCAVE: 'windcave',
    CASH: 'cash',
}

// Payment provider configuration (will be synced from backend)
const activeProvider = ref(PAYMENT_PROVIDERS.STRIPE_TERMINAL)
const providerConfig = ref({})
const currentProviderRef = ref(null)
const paymentStatusRef = ref('idle')
const lastTransactionRef = ref(null)
const errorRef = ref(null)
const statusMessageRef = ref('')

// Payment status enum
export const PAYMENT_STATUS = {
    IDLE: 'idle',
    INITIALIZING: 'initializing',
    WAITING_FOR_CARD: 'waiting_for_card',
    PROCESSING: 'processing',
    SIGNATURE_REQUIRED: 'signature_required',
    SUCCESS: 'success',
    FAILED: 'failed',
    CANCELLED: 'cancelled',
}

/**
 * Base Payment Provider Interface
 * All providers must implement these methods
 */
class PaymentProviderBase {
    async initialize(config) {
        throw new Error('initialize() must be implemented')
    }

    async processPayment(amount, metadata) {
        throw new Error('processPayment() must be implemented')
    }

    async cancelPayment() {
        throw new Error('cancelPayment() must be implemented')
    }

    async refundPayment(transactionId, amount) {
        throw new Error('refundPayment() must be implemented')
    }

    async getStatus() {
        throw new Error('getStatus() must be implemented')
    }

    async disconnect() {
        throw new Error('disconnect() must be implemented')
    }
}

/**
 * Stripe Terminal Provider (Simulated)
 */
class StripeTerminalProvider extends PaymentProviderBase {
    constructor() {
        super()
        this.status = PAYMENT_STATUS.IDLE
        this.terminal = null
    }

    async initialize(config) {
        console.log('Initializing Stripe Terminal (Simulated)...', config)
        this.status = PAYMENT_STATUS.INITIALIZING

        // Simulate terminal initialization
        await new Promise(resolve => setTimeout(resolve, 1000))

        this.terminal = {
            id: 'simulated_terminal_001',
            status: 'online',
        }

        this.status = PAYMENT_STATUS.IDLE
        return {success: true, terminal: this.terminal}
    }

    async processPayment(amount, metadata = {}) {
        console.log('Processing Stripe Terminal payment:', {amount, metadata})
        this.status = PAYMENT_STATUS.WAITING_FOR_CARD

        try {
            const {useUserStore} = await import('../stores/user.js'); const userStore = useUserStore(); const businessId = userStore.activeBusinessId
            if (!businessId) {
                throw new Error('No business selected')
            }

            const {useDeviceRegistration} = await import('./useDeviceRegistration.js')
            const {getOrCreateDeviceUuid} = useDeviceRegistration()
            const deviceUuid = await getOrCreateDeviceUuid()
            console.log('[StripeTerminal] Using device UUID:', deviceUuid)

            const response = await useApi('/api/payment-terminals/initiate', {
                businessId: Number(businessId),
                orderId: metadata.orderId || `order_${Date.now()}`,
                amount: amount,
                currency: 'AUD',
                preferredProvider: 'stripe_terminal',
                deviceUuid: deviceUuid
            }, {
                method: 'POST'
            }, 2)

            this.status = PAYMENT_STATUS.PROCESSING

            if (response.success) {
                // Check if payment is immediately approved or still processing
                const isApproved = response.status === 'approved'
                const isProcessing = response.status === 'processing' || response.status === 'pending'

                if (isApproved) {
                    // Payment completed immediately (Tyro, Windcave, Linkly)
                    this.status = PAYMENT_STATUS.SUCCESS
                    console.log('Payment approved immediately')
                    return {
                        success: true,
                        transactionId: response.transactionId,
                        amount,
                        cardBrand: response.cardDetails?.cardType || 'Visa',
                        last4: response.cardDetails?.last4 || response.last4 || '****',
                        cardType: response.cardDetails?.cardType,
                        authCode: response.authCode,
                        aid: response.aid,
                        receipt: response.receipt,
                        timestamp: new Date().toISOString(),
                        metadata,
                        paymentId: response.paymentId,
                        surchargeAmount: response.surchargeAmount
                    }
                } else if (isProcessing) {
                    // Payment initiated, waiting for webhook (Stripe Terminal)
                    console.log('Payment processing, waiting for webhook confirmation...')
                    return {
                        success: true,
                        pending: true,
                        transactionId: response.transactionId,
                        amount,
                        cardBrand: 'Visa',
                        last4: response.last4 || '****',
                        timestamp: new Date().toISOString(),
                        metadata,
                        paymentId: response.paymentId,
                        message: 'Payment processing - waiting for confirmation'
                    }
                } else {
                    // Payment failed or declined
                    this.status = PAYMENT_STATUS.FAILED
                    return {
                        success: false,
                        error: response.message || `Payment ${response.status}`,
                        errorCode: response.status || 'payment_failed',
                    }
                }
            } else {
                this.status = PAYMENT_STATUS.FAILED
                return {
                    success: false,
                    error: response.message || 'Payment failed',
                    errorCode: 'payment_failed',
                }
            }
        } catch (error) {
            this.status = PAYMENT_STATUS.FAILED
            console.error('Payment terminal error:', error)
            const errorMessage = error.data?.message || error.message || 'Payment processing failed'
            const isNoTerminal = errorMessage.toLowerCase().includes('no enabled payment terminal')
            return {
                success: false,
                error: isNoTerminal
                    ? 'No payment terminal configured. Please set up a terminal in Business Settings > Payment Terminals.'
                    : errorMessage,
                errorCode: isNoTerminal ? 'no_terminal_configured' : 'terminal_error',
            }
        }
    }

    async cancelPayment() {
        console.log('Cancelling Stripe Terminal payment...')
        this.status = PAYMENT_STATUS.CANCELLED
        return {success: true}
    }

    async refundPayment(transactionId, amount) {
        console.log('Refunding Stripe payment:', {transactionId, amount})

        await new Promise(resolve => setTimeout(resolve, 2000))

        return {
            success: true,
            refundId: `refund_${Date.now()}`,
            amount,
            timestamp: new Date().toISOString(),
        }
    }

    async getStatus() {
        return {
            status: this.status,
            terminal: this.terminal,
        }
    }

    async disconnect() {
        console.log('Disconnecting Stripe Terminal...')
        this.terminal = null
        this.status = PAYMENT_STATUS.IDLE
        return {success: true}
    }
}

/**
 * Tyro Terminal Provider (iClient-based)
 */
class TyroTerminalProvider extends PaymentProviderBase {
    constructor() {
        super()
        this.status = PAYMENT_STATUS.IDLE
        this.tyro = null
        this.config = null
        this.statusCallback = null
        this.printer = null
        this.receiptCallback = null
        this.questionCallback = null
    }

    async initialize(config) {
        console.log('[Tyro] Initializing...', config)
        this.status = PAYMENT_STATUS.INITIALIZING
        this.config = config

        this.tyro = useTyro()
        this.printer = usePrinter()

        try {
            const environment = config.isSandbox ? 'simulator' : 'production'
            console.log('[Tyro] Environment:', environment, 'API Key:', config.apiKey ? '***' + config.apiKey.slice(-4) : 'none')
            await this.tyro.loadScript(environment)
            await this.tyro.initialize(config.apiKey, environment)

            this.status = PAYMENT_STATUS.IDLE
            console.log('[Tyro] Initialized successfully. Pairing will be handled by Tyro UI when transacting.')
            return {success: true, provider: 'tyro'}
        } catch (error) {
            console.error('[Tyro] Initialization failed:', error)
            this.status = PAYMENT_STATUS.FAILED
            throw error
        }
    }

    async processPayment(amount, metadata = {}) {
        console.log('[Tyro] processPayment called, this.tyro:', !!this.tyro)
        console.log('[Tyro] isInitialized ref:', this.tyro?.isInitialized)
        console.log('[Tyro] isInitialized.value:', this.tyro?.isInitialized?.value)

        // Also check global useTyro state
        const tyroState = useTyro()
        console.log('[Tyro] Global isInitialized.value:', tyroState.isInitialized.value)

        if (!tyroState.isInitialized.value) {
            throw new Error('Tyro not initialized')
        }

        console.log('[Tyro] Processing payment:', {amount, metadata})
        this.status = PAYMENT_STATUS.WAITING_FOR_CARD

        const terminalSettings = this.config?.additionalConfig?.tyroSettings || {}
        const tyroStore = useTyroStore()
        tyroStore.loadSettings()
        const tyroSettings = {
            ...tyroStore.settings,
            ...terminalSettings,
        }
        console.log('[Tyro] Using settings (terminal-specific):', tyroSettings)

        const { useTyroRecovery } = await import('./useTyroRecovery.js')
        const tyroRecovery = useTyroRecovery()

        if (metadata.orderId) {
            await tyroRecovery.storePendingTransaction(metadata.orderId, amount)
        }

        try {
            const useRetry = metadata.enableRetry !== false
            const purchaseMethod = useRetry
                ? this.tyro.initiatePurchaseWithRetry
                : this.tyro.initiatePurchase

            const result = await purchaseMethod.call(this.tyro, amount, {
                terminalId: this.config?.terminalId,
                merchantId: this.config?.merchantId,
                integratedReceipt: true,
                enableSurcharge: tyroSettings.integratedSurcharge,
                onStatusUpdate: (message) => {
                    console.log('[Tyro] Status update:', message)
                    if (this.statusCallback) {
                        this.statusCallback(message)
                    }
                },
                onReceipt: async (receipt) => {
                    console.log('[Tyro] Receipt received:', redactSensitive(receipt))
                    if (receipt.signatureRequired) {
                        console.log('[Tyro] Signature required on receipt - printing immediately for signature')
                        this.status = PAYMENT_STATUS.SIGNATURE_REQUIRED
                        this._signatureRequired = true
                        this._signatureReceiptPrinted = true

                        if (this.printer && receipt.merchantReceipt) {
                            try {
                                await this.printer.printTerminalReceipt(receipt.merchantReceipt, {
                                    title: 'SIGNATURE REQUIRED - MERCHANT COPY',
                                })
                                console.log('[Tyro] Signature receipt printed successfully')
                            } catch (printError) {
                                console.warn('[Tyro] Failed to print signature receipt:', printError)
                            }
                        }
                    } else if (this.printer && receipt.merchantReceipt && !this._merchantReceiptPrinted) {
                        // Set flag BEFORE await to prevent race condition:
                        // onReceipt is fire-and-forget, so the completion handler can run
                        // while this async callback is still awaiting the print call
                        this._merchantReceiptPrinted = true
                        try {
                            await this.printer.printTerminalReceipt(receipt.merchantReceipt, {
                                title: 'MERCHANT COPY',
                            })
                            console.log('[Tyro] Mid-transaction receipt printed (DCC/other)')
                        } catch (printError) {
                            console.warn('[Tyro] Failed to print mid-transaction receipt:', printError)
                        }
                    }
                    if (this.receiptCallback) {
                        this.receiptCallback(receipt)
                    }
                },
                onQuestion: (question, answerCallback) => {
                    console.log('[Tyro] Question received:', question.text, question.options)
                    if (this.questionCallback) {
                        this.questionCallback(question, answerCallback)
                    } else {
                        console.log('[Tyro] No question handler set, auto-answering:', question.options[0])
                        answerCallback(question.options[0])
                    }
                },
                onRetry: (attempt, maxRetries, error) => {
                    console.log(`[Tyro] Retry ${attempt}/${maxRetries} due to: ${error.message}`)
                    if (this.statusCallback) {
                        this.statusCallback(`Retrying... (${attempt}/${maxRetries})`)
                    }
                },
                maxRetries: metadata.maxRetries || 3,
                timeout: metadata.timeout || 120000,
            })

            this.status = PAYMENT_STATUS.SUCCESS

            await tyroRecovery.clearPendingTransaction()
            await this.logTransaction(result, metadata)

            if (this.printer) {
                const signatureReceiptAlreadyPrinted = this._signatureReceiptPrinted || false
                const merchantReceiptAlreadyPrinted = this._merchantReceiptPrinted || false
                this._signatureRequired = false
                this._signatureReceiptPrinted = false
                this._merchantReceiptPrinted = false

                if (result.merchantReceipt && !signatureReceiptAlreadyPrinted && !merchantReceiptAlreadyPrinted) {
                    if (tyroSettings.printMerchantCopy) {
                        try {
                            await this.printer.printTerminalReceipt(result.merchantReceipt, {
                                title: 'MERCHANT COPY',
                            })
                        } catch (printError) {
                            console.warn('[Tyro] Failed to print merchant receipt:', printError)
                        }
                    }
                }

                if (result.customerReceipt && tyroSettings.printCustomerCopy) {
                    try {
                        await this.printer.printTerminalReceipt(result.customerReceipt, {
                            title: 'CUSTOMER COPY',
                        })
                    } catch (printError) {
                        console.warn('[Tyro] Failed to print customer receipt:', printError)
                    }
                }
            }

            return {
                success: true,
                transactionId: result.transactionId,
                amount: result.amount,
                cardBrand: result.cardType,
                last4: result.last4,
                timestamp: new Date().toISOString(),
                metadata,
                customerReceipt: result.customerReceipt,
                merchantReceipt: result.merchantReceipt,
                surchargeAmount: result.surchargeAmount,
            }
        } catch (error) {
            this.status = PAYMENT_STATUS.FAILED
            console.error('[Tyro] Payment failed:', error)

            await tyroRecovery.clearPendingTransaction()

            const customerReceipt = error.customerReceipt || error.rawResponse?.customerReceipt
            const merchantReceipt = error.merchantReceipt || error.rawResponse?.merchantReceipt
            const failReason = error.result || 'FAILED'

            if (this.printer) {
                if (merchantReceipt && !this._merchantReceiptPrinted && !this._signatureReceiptPrinted) {
                    try {
                        await this.printer.printTerminalReceipt(merchantReceipt, {
                            title: `${failReason} - MERCHANT COPY`,
                        })
                    } catch (printError) {
                        console.warn('[Tyro] Failed to print failed transaction merchant receipt:', printError)
                    }
                }

                if (customerReceipt) {
                    try {
                        await this.printer.printTerminalReceipt(customerReceipt, {
                            title: `${failReason} - CUSTOMER COPY`,
                        })
                    } catch (printError) {
                        console.warn('[Tyro] Failed to print failed transaction customer receipt:', printError)
                    }
                }
            }

            this._merchantReceiptPrinted = false
            this._signatureReceiptPrinted = false

            await this.logTransaction({
                transactionId: `failed_${Date.now()}`,
                status: failReason,
                amount,
                success: false,
                customerReceipt,
                merchantReceipt,
            }, metadata)

            return {
                success: false,
                error: error.message || error.result || 'Payment failed',
                errorCode: error.result || 'tyro_error',
                customerReceipt,
            }
        }
    }

    async logTransaction(result, metadata) {
        try {
            const {useUserStore} = await import('../stores/user.js')
            const userStore = useUserStore()

            await useApi('/api/payment-terminals/log-transaction', {
                businessId: Number(userStore.activeBusinessId),
                orderId: metadata.orderId,
                transactionId: result.transactionId,
                status: result.success !== false ? 'APPROVED' : 'FAILED',
                amount: result.amount,
                cardType: result.cardType,
                last4: result.last4,
                customerReceipt: result.customerReceipt,
                merchantReceipt: result.merchantReceipt,
                surchargeAmount: result.surchargeAmount,
                provider: 'tyro',
                rawResponse: result.rawResponse,
            }, {method: 'POST'}, 2)

            console.log('[Tyro] Transaction logged to backend')
        } catch (logError) {
            console.error('[Tyro] Failed to log transaction:', logError)
        }

        try {
            const { usePosDatabase } = await import('./usePosDatabase.js')
            const { saveTerminalLog, isDbReady } = usePosDatabase()
            if (isDbReady.value) {
                await saveTerminalLog({
                    provider: 'tyro',
                    txnType: metadata.txnType || 'Purchase',
                    txnRef: result.transactionId,
                    status: result.success !== false ? 'approved' : 'declined',
                    amount: result.amount,
                    responseText: result.cardType ? `${result.cardType} ****${result.last4 || ''}` : null,
                    orderId: metadata.orderId,
                    parsedResponse: result.rawResponse,
                })
            }
        } catch {}
    }

    setReceiptCallback(callback) {
        this.receiptCallback = callback
    }

    setQuestionCallback(callback) {
        this.questionCallback = callback
    }

    async cancelPayment() {
        if (!this.tyro) {
            return {success: false, message: 'Tyro not initialized'}
        }

        console.log('[Tyro] Cancelling payment...')
        const cancelled = this.tyro.cancelTransaction()
        if (cancelled) {
            this.status = PAYMENT_STATUS.CANCELLED
        }
        return {success: cancelled}
    }

    async refundPayment(transactionId, amount) {
        const tyroState = useTyro()
        console.log('[Tyro] refundPayment - Global isInitialized.value:', tyroState.isInitialized.value)

        if (!tyroState.isInitialized.value) {
            throw new Error('Tyro not initialized')
        }

        const terminalSettings = this.config?.additionalConfig?.tyroSettings || {}
        const tyroStore = useTyroStore()
        tyroStore.loadSettings()
        const tyroSettings = {
            ...tyroStore.settings,
            ...terminalSettings,
        }

        console.log('[Tyro] Processing refund:', {transactionId, amount})

        let signatureReceiptPrinted = false
        let merchantReceiptPrinted = false

        try {
            const result = await tyroState.initiateRefund(amount, {
                terminalId: this.config?.terminalId,
                merchantId: this.config?.merchantId,
                onStatusUpdate: (message) => {
                    console.log('[Tyro] Refund status:', message)
                    if (this.statusCallback) {
                        this.statusCallback(message)
                    }
                },
                onReceipt: async (receipt) => {
                    console.log('[Tyro] Refund receipt received:', redactSensitive(receipt))
                    if (receipt.signatureRequired) {
                        console.log('[Tyro] Refund signature required - printing immediately')
                        signatureReceiptPrinted = true

                        if (this.printer && receipt.merchantReceipt) {
                            try {
                                await this.printer.printTerminalReceipt(receipt.merchantReceipt, {
                                    title: 'SIGNATURE REQUIRED - REFUND MERCHANT COPY',
                                })
                                console.log('[Tyro] Refund signature receipt printed successfully')
                            } catch (printError) {
                                console.warn('[Tyro] Failed to print refund signature receipt:', printError)
                            }
                        }
                    } else if (this.printer && receipt.merchantReceipt && !merchantReceiptPrinted) {
                        // Set flag BEFORE await to prevent race condition:
                        // onReceipt is fire-and-forget, so the completion handler can run
                        // while this async callback is still awaiting the print call
                        merchantReceiptPrinted = true
                        try {
                            await this.printer.printTerminalReceipt(receipt.merchantReceipt, {
                                title: 'REFUND - MERCHANT COPY',
                            })
                            console.log('[Tyro] Refund mid-transaction receipt printed (DCC/other)')
                        } catch (printError) {
                            console.warn('[Tyro] Failed to print refund mid-transaction receipt:', printError)
                        }
                    }
                },
            })

            if (this.printer && !signatureReceiptPrinted && !merchantReceiptPrinted) {
                if (result.merchantReceipt && tyroSettings.printMerchantCopy) {
                    try {
                        await this.printer.printTerminalReceipt(result.merchantReceipt, {
                            title: 'REFUND - MERCHANT COPY',
                        })
                    } catch (printError) {
                        console.warn('[Tyro] Failed to print refund merchant receipt:', printError)
                    }
                }

                if (result.customerReceipt && tyroSettings.printCustomerCopy) {
                    try {
                        await this.printer.printTerminalReceipt(result.customerReceipt, {
                            title: 'REFUND - CUSTOMER COPY',
                        })
                    } catch (printError) {
                        console.warn('[Tyro] Failed to print refund customer receipt:', printError)
                    }
                }
            } else if (this.printer && signatureReceiptPrinted) {
                if (result.customerReceipt && tyroSettings.printCustomerCopy) {
                    try {
                        await this.printer.printTerminalReceipt(result.customerReceipt, {
                            title: 'REFUND - CUSTOMER COPY',
                        })
                    } catch (printError) {
                        console.warn('[Tyro] Failed to print refund customer receipt:', printError)
                    }
                }
            }

            return {
                success: true,
                refundId: result.refundId,
                amount: result.amount,
                cardType: result.cardType,
                last4: result.last4,
                customerReceipt: result.customerReceipt,
                merchantReceipt: result.merchantReceipt,
                receipt: result.receipt || result.customerReceipt || result.merchantReceipt,
                timestamp: new Date().toISOString(),
            }
        } catch (error) {
            console.error('[Tyro] Refund failed:', error)

            const merchantReceipt = error.merchantReceipt || error.rawResponse?.merchantReceipt
            const customerReceipt = error.customerReceipt || error.rawResponse?.customerReceipt

            if (this.printer) {
                if (merchantReceipt && !merchantReceiptPrinted && !signatureReceiptPrinted) {
                    try {
                        await this.printer.printTerminalReceipt(merchantReceipt, {
                            title: 'REFUND DECLINED - MERCHANT COPY',
                        })
                    } catch (printError) {
                        console.warn('[Tyro] Failed to print declined refund merchant receipt:', printError)
                    }
                }

                if (customerReceipt) {
                    try {
                        await this.printer.printTerminalReceipt(customerReceipt, {
                            title: 'REFUND DECLINED - CUSTOMER COPY',
                        })
                    } catch (printError) {
                        console.warn('[Tyro] Failed to print declined refund customer receipt:', printError)
                    }
                }
            }

            throw error
        }
    }

    async getStatus() {
        return {
            status: this.status,
            tyroStatus: this.tyro?.currentStatus.value,
            isPaired: this.tyro?.isPaired.value,
            isProcessing: this.tyro?.isProcessing.value,
        }
    }

    async disconnect() {
        console.log('[Tyro] Disconnecting...')
        if (this.tyro) {
            this.tyro.reset()
        }
        this.status = PAYMENT_STATUS.IDLE
        return {success: true}
    }

    setStatusCallback(callback) {
        this.statusCallback = callback
    }
}

/**
 * Tyro Kiosk Provider (Headless iClient for self-service kiosk)
 * Uses TYRO.IClient() instead of IClientWithUI for certification compliance
 */
class TyroKioskProvider extends PaymentProviderBase {
    constructor() {
        super()
        this.status = PAYMENT_STATUS.IDLE
        this.tyroKiosk = null
        this.tyroKioskPairing = null
        this.config = null
        this.statusCallback = null
        this.printer = null
    }

    _resolveEnvironment(config) {
        if (config.additionalConfig?.tyroEnvironment) return config.additionalConfig.tyroEnvironment
        return config.isSandbox ? 'simulator' : 'production'
    }

    async initialize(config) {
        console.log('[TyroKiosk] Initializing with config from backend...', config)
        this.status = PAYMENT_STATUS.INITIALIZING
        this.config = config
        this.printer = usePrinter()

        try {
            const {useTyroKiosk} = await import('./useTyroKiosk.js')
            const {useTyroKioskPairing} = await import('./useTyroKioskPairing.js')
            this.tyroKiosk = useTyroKiosk()
            this.tyroKioskPairing = useTyroKioskPairing()

            if (!config.apiKey || !config.merchantId || !config.terminalId) {
                throw new Error('Tyro terminal not configured. Please assign a terminal from POS.')
            }

            const environment = this._resolveEnvironment(config)
            await this.tyroKiosk.loadScript(environment)
            await this.tyroKiosk.initialize(config.apiKey, environment)

            // Reuse a previously-paired integration key for this MID/TID so
            // we don't prompt the terminal operator to re-authorise on every
            // app boot. Only pair from scratch if we don't have a matching
            // stored key.
            const stored = await this.tyroKioskPairing.loadStoredCredentials()
            const storedKeyMatchesTerminal =
                stored &&
                stored.integrationKey &&
                String(stored.mid) === String(config.merchantId) &&
                String(stored.tid) === String(config.terminalId)

            if (storedKeyMatchesTerminal) {
                console.log('[TyroKiosk] Reusing stored integration key - MID:', config.merchantId, 'TID:', config.terminalId)
                this.config.integrationKey = stored.integrationKey
                await this.tyroKioskPairing.markAsPaired()
            } else {
                console.log('[TyroKiosk] No matching stored key - performing headless pairing (MID:', config.merchantId, 'TID:', config.terminalId, ')')
                try {
                    const pairResult = await this.tyroKiosk.pairTerminal(
                        config.merchantId,
                        config.terminalId
                    )
                    if (pairResult.success) {
                        const freshKey = pairResult.integrationKey || ''
                        this.config.integrationKey = freshKey
                        await this.tyroKioskPairing.saveCredentials(
                            config.merchantId, config.terminalId, freshKey, config.apiKey, environment
                        )
                        await this.tyroKioskPairing.markAsPaired()
                        console.log('[TyroKiosk] Headless pairing successful - integration key stored')
                    }
                } catch (pairError) {
                    console.error('[TyroKiosk] Headless pairing failed:', pairError.message)
                    this.tyroKioskPairing.setPairingError(pairError.message)
                    throw pairError
                }
            }

            console.log('[TyroKiosk] Initialized with backend config - MID:', config.merchantId, 'TID:', config.terminalId)

            this.status = PAYMENT_STATUS.IDLE
            return {success: true, provider: 'tyro_kiosk'}
        } catch (error) {
            console.error('[TyroKiosk] Initialization failed:', error)
            this.status = PAYMENT_STATUS.FAILED
            throw error
        }
    }

    async processPayment(amount, metadata = {}) {
        console.log('[TyroKiosk] Processing payment:', {amount, metadata})
        this.status = PAYMENT_STATUS.WAITING_FOR_CARD

        if (!this.config?.merchantId || !this.config?.terminalId || !this.config?.integrationKey) {
            throw new Error('Tyro terminal not configured. Please assign a terminal from POS.')
        }

        const environment = this._resolveEnvironment(this.config)

        if (!this.tyroKiosk?.isInitialized?.value) {
            await this.tyroKiosk.loadScript(environment)
            await this.tyroKiosk.initialize(this.config.apiKey, environment)
        }

        try {
            const result = await this.tyroKiosk.initiatePurchase(amount, {
                mid: this.config.merchantId,
                tid: this.config.terminalId,
                integrationKey: this.config.integrationKey,
                enableSurcharge: true,
                // Pass orderId so initiatePurchase can derive a deterministic
                // transactionId — retries of the same order will use the same
                // Tyro txn id, allowing Tyro to dedupe and enabling
                // continueLastTransaction recovery to find the prior attempt.
                orderId: metadata.orderId,
                onStatusUpdate: (message) => {
                    console.log('[TyroKiosk] Status:', message)
                    if (this.statusCallback) {
                        this.statusCallback(message)
                    }
                },
                onSignatureRequired: () => {
                    console.log('[TyroKiosk] Signature required - will auto-cancel')
                    this.status = PAYMENT_STATUS.SIGNATURE_REQUIRED
                },
                onReceipt: (receipt) => {
                    console.log('[TyroKiosk] Receipt received:', redactSensitive(receipt))
                },
            })

            this.status = PAYMENT_STATUS.SUCCESS

            try {
                const {useUserStore} = await import('../stores/user.js')
                const userStore = useUserStore()
                await useApi('/api/payment-terminals/log-transaction', {
                    businessId: Number(userStore.activeBusinessId),
                    orderId: metadata.orderId,
                    transactionId: result.transactionId,
                    status: 'APPROVED',
                    amount: result.amount,
                    cardType: result.cardType,
                    last4: result.last4,
                    customerReceipt: result.customerReceipt,
                    merchantReceipt: result.merchantReceipt,
                    surchargeAmount: result.surchargeAmount,
                    provider: 'tyro',
                    rawResponse: result.rawResponse,
                }, {method: 'POST'}, 2)
                console.log('[TyroKiosk] Transaction logged to backend')
            } catch (logError) {
                console.error('[TyroKiosk] Failed to log transaction:', logError)
            }

            return {
                success: true,
                transactionId: result.transactionId,
                amount: result.amount,
                cardBrand: result.cardType,
                last4: result.last4,
                timestamp: new Date().toISOString(),
                metadata,
                customerReceipt: result.customerReceipt,
                merchantReceipt: result.merchantReceipt,
                surchargeAmount: result.surchargeAmount,
            }
        } catch (error) {
            this.status = PAYMENT_STATUS.FAILED
            console.error('[TyroKiosk] Payment failed:', error)

            if (error.signatureRequired) {
                return {
                    success: false,
                    error: 'This transaction requires signature verification. Please see staff for assistance.',
                    errorCode: 'signature_required',
                    signatureRequired: true,
                }
            }

            if (error.result === 'CANCELLED' || error.result === 'REVERSED') {
                try {
                    const {useUserStore} = await import('../stores/user.js')
                    const userStore = useUserStore()
                    await useApi('/api/payment-terminals/log-transaction', {
                        businessId: Number(userStore.activeBusinessId),
                        orderId: metadata.orderId,
                        transactionId: error.rawResponse?.transactionReference || null,
                        status: error.result,
                        amount: amount,
                        customerReceipt: error.customerReceipt,
                        merchantReceipt: error.merchantReceipt,
                        provider: 'tyro',
                        rawResponse: error.rawResponse,
                    }, {method: 'POST'}, 2)
                    console.log(`[TyroKiosk] ${error.result} transaction logged to backend`)
                } catch (logError) {
                    console.error(`[TyroKiosk] Failed to log ${error.result} transaction:`, logError)
                }
            }

            // NOT STARTED means the terminal was busy with a stale transaction
            // and never accepted this purchase. Issue a cancel to clear the
            // stuck transaction so the next attempt can proceed, and return
            // a user-friendly error instead of "NOT STARTED".
            if (error.result === 'NOT STARTED') {
                try {
                    console.log('[TyroKiosk] NOT STARTED - cancelling stale transaction on terminal')
                    this.tyroKiosk.cancelTransaction()
                } catch (cancelError) {
                    console.warn('[TyroKiosk] Cleanup cancel failed (safe to ignore):', cancelError.message)
                }
                return {
                    success: false,
                    error: 'Terminal was busy with a previous transaction. Please try again.',
                    errorCode: 'TERMINAL_BUSY',
                    cancelled: false,
                }
            }

            return {
                success: false,
                error: error.message || error.result || 'Payment failed',
                errorCode: error.result || 'tyro_kiosk_error',
                customerReceipt: error.customerReceipt,
                merchantReceipt: error.merchantReceipt,
                cancelled: error.result === 'CANCELLED',
                reversed: error.result === 'REVERSED',
            }
        }
    }

    async cancelPayment() {
        console.log('[TyroKiosk] Cancelling payment...')
        if (this.tyroKiosk) {
            this.tyroKiosk.cancelTransaction()
        }
        this.status = PAYMENT_STATUS.CANCELLED
        return {success: true}
    }

    async refundPayment(transactionId, amount) {
        console.log('[TyroKiosk] Refunds not available on kiosk')
        throw new Error('Refunds are not available on kiosk. Please process at counter.')
    }

    async getStatus() {
        return {
            status: this.status,
            tyroStatus: this.tyroKiosk?.currentStatus?.value,
            isPaired: this.tyroKioskPairing?.isPaired?.value,
            isProcessing: this.tyroKiosk?.isProcessing?.value,
        }
    }

    async disconnect() {
        console.log('[TyroKiosk] Disconnecting...')
        if (this.tyroKiosk) {
            this.tyroKiosk.reset()
        }
        this.status = PAYMENT_STATUS.IDLE
        return {success: true}
    }

    setStatusCallback(callback) {
        this.statusCallback = callback
    }
}

/**
 * Windcave HIT Provider (via backend API)
 */
class WindcaveProvider extends PaymentProviderBase {
    constructor() {
        super()
        this.status = PAYMENT_STATUS.IDLE
        this.config = null
        this.statusCallback = null
        this.signatureCallback = null
        this.lastTransactionId = null
        this.printer = null
    }

    async initialize(config) {
        console.log('[Windcave] Initializing...', config)
        this.status = PAYMENT_STATUS.INITIALIZING
        this.config = config
        this.printer = usePrinter()
        this.status = PAYMENT_STATUS.IDLE
        return {success: true, provider: 'windcave'}
    }

    async _saveLocalTerminalLog(data) {
        try {
            const { usePosDatabase } = await import('./usePosDatabase.js')
            const { saveTerminalLog, isDbReady } = usePosDatabase()
            if (!isDbReady.value) return
            await saveTerminalLog({ provider: 'windcave', ...data })
        } catch {}
    }

    async printWindcaveReceipt(receiptContent) {
        if (!receiptContent || !this.printer) return

        try {
            console.log('[Windcave] Printing receipt from terminal')
            await this.printer.printTerminalReceipt(receiptContent, {
                title: 'PAYMENT RECEIPT'
            })
        } catch (printError) {
            console.warn('[Windcave] Failed to print receipt:', printError)
        }
    }

    async processPayment(amount, metadata = {}) {
        console.log('[Windcave] Processing payment via backend:', {amount, metadata})
        this.status = PAYMENT_STATUS.WAITING_FOR_CARD

        if (this.statusCallback) {
            this.statusCallback('Waiting for card on terminal...')
        }

        try {
            const {useUserStore} = await import('../stores/user.js')
            const userStore = useUserStore()
            const businessId = userStore.activeBusinessId

            if (!businessId) {
                throw new Error('No business selected')
            }

            const {useDeviceRegistration} = await import('./useDeviceRegistration.js')
            const {getOrCreateDeviceUuid} = useDeviceRegistration()
            const deviceUuid = await getOrCreateDeviceUuid()
            console.log('[Windcave] Using device UUID:', deviceUuid)

            const { useWindcaveRecovery } = await import('./useWindcaveRecovery.js')
            const { storePendingTransaction, clearPendingTransaction } = useWindcaveRecovery()

            const requestBody = {
                businessId: Number(businessId),
                orderId: metadata.orderId || `order_${Date.now()}`,
                amount: amount,
                currency: 'AUD',
                preferredProvider: 'windcave',
                deviceUuid: deviceUuid
            }
            const response = await useApi('/api/payment-terminals/initiate', requestBody, {
                method: 'POST'
            }, 2)

            this.lastTransactionId = response.transactionId

            this._saveLocalTerminalLog({
                txnType: 'Purchase',
                txnRef: response.transactionId,
                status: response.status || 'processing',
                amount: amount,
                responseText: response.message,
                orderId: metadata.orderId,
                xmlRequest: response._xmlRequest || JSON.stringify(requestBody),
                xmlResponse: response._xmlResponse,
                parsedResponse: response,
            })

            if (response.transactionId && response.status !== 'approved' && response.status !== 'declined' && response.status !== 'failed') {
                await storePendingTransaction(response.transactionId, metadata.orderId, amount)
            }

            if (response.status === 'signature_required') {
                this.status = PAYMENT_STATUS.SIGNATURE_REQUIRED
                console.log('[Windcave] Signature required:', response.signatureButtons)

                if (this.statusCallback) {
                    this.statusCallback(response.displayLine1 || 'Verify customer signature')
                }

                return {
                    success: false,
                    signatureRequired: true,
                    transactionId: response.transactionId,
                    amount,
                    displayLine1: response.displayLine1,
                    displayLine2: response.displayLine2,
                    signatureButtons: response.signatureButtons || {
                        button1Label: 'Accept',
                        button2Label: 'Decline'
                    },
                    metadata,
                    paymentId: response.paymentId,
                    receipt: response.receipt
                }
            }

            if (response.success) {
                const isApproved = response.status === 'approved'
                const isDeclined = response.status === 'declined'
                const isFailed = response.status === 'failed'

                if (isApproved) {
                    this.status = PAYMENT_STATUS.SUCCESS
                    if (this.statusCallback) {
                        this.statusCallback('Payment approved')
                    }

                    if (response.receipt) {
                        await this.printWindcaveReceipt(response.receipt)
                    }

                    await clearPendingTransaction()

                    this._saveLocalTerminalLog({
                        txnType: 'Purchase',
                        txnRef: response.transactionId,
                        status: 'approved',
                        amount: amount,
                        responseText: response.message,
                        orderId: metadata.orderId,
                        xmlRequest: response._xmlRequest,
                        xmlResponse: response._xmlResponse,
                        parsedResponse: response,
                    })

                    return {
                        success: true,
                        transactionId: response.transactionId,
                        amount,
                        cardBrand: response.cardDetails?.cardType || response.cardType || 'Card',
                        last4: response.cardDetails?.last4 || response.last4 || '****',
                        cardType: response.cardDetails?.cardType || response.cardType,
                        authCode: response.authCode,
                        aid: response.aid,
                        timestamp: new Date().toISOString(),
                        metadata,
                        paymentId: response.paymentId,
                        surchargeAmount: response.surchargeAmount,
                        receipt: response.receipt
                    }
                } else if (isDeclined || isFailed) {
                    this.status = PAYMENT_STATUS.FAILED
                    await clearPendingTransaction()

                    this._saveLocalTerminalLog({
                        txnType: 'Purchase',
                        txnRef: response.transactionId,
                        status: response.status,
                        amount: amount,
                        responseText: response.message,
                        orderId: metadata.orderId,
                        xmlRequest: response._xmlRequest,
                        xmlResponse: response._xmlResponse,
                        parsedResponse: response,
                    })

                    return {
                        success: false,
                        error: response.message || 'Payment declined',
                        errorCode: response.status,
                    }
                } else {
                    this.status = PAYMENT_STATUS.PROCESSING
                    if (this.statusCallback) {
                        this.statusCallback(response.displayLine1 || 'Processing on terminal...')
                    }
                    return {
                        success: true,
                        pending: true,
                        transactionId: response.transactionId,
                        amount,
                        displayLine1: response.displayLine1,
                        displayLine2: response.displayLine2,
                        timestamp: new Date().toISOString(),
                        metadata,
                        paymentId: response.paymentId,
                        message: 'Payment processing'
                    }
                }
            } else {
                this.status = PAYMENT_STATUS.FAILED

                this._saveLocalTerminalLog({
                    txnType: 'Purchase',
                    txnRef: response.transactionId,
                    status: response.status || 'failed',
                    amount: amount,
                    responseText: response.message,
                    orderId: metadata.orderId,
                    xmlRequest: response._xmlRequest,
                    xmlResponse: response._xmlResponse,
                    parsedResponse: response,
                })

                return {
                    success: false,
                    error: response.message || 'Payment failed',
                    errorCode: 'payment_failed',
                }
            }
        } catch (error) {
            this.status = PAYMENT_STATUS.FAILED
            console.error('[Windcave] Payment error:', error)
            const errorMessage = error.data?.message || error.message || 'Payment processing failed'
            return {
                success: false,
                error: errorMessage,
                errorCode: 'windcave_error',
            }
        }
    }

    async confirmSignature(accepted) {
        console.log('[Windcave] Confirming signature:', {accepted, transactionId: this.lastTransactionId})

        if (!this.lastTransactionId) {
            return {
                success: false,
                error: 'No active transaction to confirm signature',
            }
        }

        try {
            const {useUserStore} = await import('../stores/user.js')
            const userStore = useUserStore()
            const businessId = userStore.activeBusinessId

            this.status = PAYMENT_STATUS.PROCESSING
            if (this.statusCallback) {
                this.statusCallback(accepted ? 'Processing signature acceptance...' : 'Processing signature decline...')
            }

            const {useDeviceRegistration} = await import('./useDeviceRegistration.js')
            const {getOrCreateDeviceUuid} = useDeviceRegistration()
            const deviceUuid = await getOrCreateDeviceUuid()

            const sigRequestBody = {
                businessId: Number(businessId),
                transactionId: this.lastTransactionId,
                accepted: accepted,
                deviceUuid: deviceUuid
            }
            const response = await useApi('/api/payment-terminals/confirm-signature', sigRequestBody, {method: 'POST'}, 2)

            if (response.status === 'approved') {
                this.status = PAYMENT_STATUS.SUCCESS
                if (this.statusCallback) {
                    this.statusCallback('Payment approved')
                }

                this._saveLocalTerminalLog({
                    txnType: 'SignatureResult',
                    txnRef: response.transactionId || this.lastTransactionId,
                    status: 'approved',
                    responseText: response.message,
                    xmlRequest: response._xmlRequest || JSON.stringify(sigRequestBody),
                    xmlResponse: response._xmlResponse,
                    parsedResponse: response,
                })

                return {
                    success: true,
                    transactionId: response.transactionId,
                    status: 'approved',
                    message: response.message,
                    authCode: response.authCode,
                    aid: response.aid,
                    surchargeAmount: response.surchargeAmount,
                    cardType: response.cardDetails?.cardType,
                    last4: response.cardDetails?.last4,
                    receipt: response.receipt
                }
            } else if (response.status === 'processing') {
                console.log('[Windcave] Signature sent, polling for completion with ans parameter')
                const signatureAnswer = accepted ? '1' : '2'
                const pollResult = await this.pollForStatus(response.transactionId, null, signatureAnswer)

                this._saveLocalTerminalLog({
                    txnType: 'SignatureResult',
                    txnRef: response.transactionId || this.lastTransactionId,
                    status: pollResult.success ? 'approved' : (pollResult.status || 'declined'),
                    responseText: pollResult.error || pollResult.message,
                    xmlRequest: response._xmlRequest || JSON.stringify(sigRequestBody),
                    xmlResponse: response._xmlResponse,
                    parsedResponse: pollResult,
                })

                return pollResult
            } else {
                this.status = PAYMENT_STATUS.FAILED

                this._saveLocalTerminalLog({
                    txnType: 'SignatureResult',
                    txnRef: response.transactionId || this.lastTransactionId,
                    status: 'declined',
                    responseText: response.message || 'Signature declined',
                    xmlRequest: response._xmlRequest || JSON.stringify(sigRequestBody),
                    xmlResponse: response._xmlResponse,
                    parsedResponse: response,
                })

                return {
                    success: false,
                    transactionId: response.transactionId,
                    status: response.status,
                    error: response.message || 'Signature declined'
                }
            }
        } catch (error) {
            this.status = PAYMENT_STATUS.FAILED
            console.error('[Windcave] Signature confirmation error:', error)
            return {
                success: false,
                error: error.data?.message || error.message || 'Signature confirmation failed',
            }
        }
    }

    async pollForStatus(transactionId, onStatusUpdate, signatureAnswer = null, deviceType = null) {
        const POLL_INTERVAL = 1500
        const MAX_ATTEMPTS = signatureAnswer ? 180 : 60

        const { useWindcaveRecovery } = await import('./useWindcaveRecovery.js')
        const { clearPendingTransaction } = useWindcaveRecovery()

        console.log('[Windcave] Starting status polling for:', transactionId, signatureAnswer ? '(with signature)' : '', deviceType ? `(${deviceType})` : '')

        const {useDeviceRegistration} = await import('./useDeviceRegistration.js')
        const {getOrCreateDeviceUuid} = useDeviceRegistration()
        const deviceUuid = await getOrCreateDeviceUuid()

        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
            try {
                let url = `/api/payment-terminals/status/${transactionId}?deviceUuid=${deviceUuid}`
                if (signatureAnswer) {
                    url += `&signatureAnswer=${signatureAnswer}`
                }
                if (deviceType) {
                    url += `&deviceType=${deviceType}`
                }
                const response = await useApi(url, {}, {method: 'GET'}, 2)

                console.log('[Windcave] Status poll response:', redactSensitive(response))

                if (onStatusUpdate) {
                    onStatusUpdate({
                        displayLine1: response.displayLine1,
                        displayLine2: response.displayLine2,
                        status: response.status
                    })
                }

                if (this.statusCallback) {
                    const statusText = response.displayLine1 || response.displayLine2 || 'Processing...'
                    this.statusCallback(statusText)
                }

                if (response.status === 'approved') {
                    this.status = PAYMENT_STATUS.SUCCESS

                    if (response.receipt && deviceType !== 'kiosk' && !signatureAnswer) {
                        await this.printWindcaveReceipt(response.receipt)
                    }

                    await clearPendingTransaction()

                    this._saveLocalTerminalLog({
                        txnType: 'PollResult',
                        txnRef: response.transactionId || transactionId,
                        status: 'approved',
                        responseText: response.message,
                        xmlRequest: response._xmlRequest,
                        xmlResponse: response._xmlResponse,
                        parsedResponse: response,
                    })

                    return {
                        success: true,
                        transactionId: response.transactionId || transactionId,
                        status: 'approved',
                        authCode: response.authCode,
                        aid: response.aid,
                        surchargeAmount: response.surchargeAmount,
                        cardType: response.cardDetails?.cardType,
                        last4: response.cardDetails?.last4,
                        receipt: response.receipt
                    }
                }

                if (response.status === 'declined' || response.status === 'failed' || response.status === 'cancelled') {
                    this.status = PAYMENT_STATUS.FAILED
                    await clearPendingTransaction()

                    if (response.receipt && deviceType !== 'kiosk') {
                        await this.printWindcaveReceipt(response.receipt)
                    }

                    this._saveLocalTerminalLog({
                        txnType: 'PollResult',
                        txnRef: response.transactionId || transactionId,
                        status: response.status,
                        responseText: response.message,
                        xmlRequest: response._xmlRequest,
                        xmlResponse: response._xmlResponse,
                        parsedResponse: response,
                    })

                    return {
                        success: false,
                        error: response.message || 'Payment declined',
                        status: response.status,
                        receipt: response.receipt
                    }
                }

                if (response.signatureRequired && !signatureAnswer) {
                    if (response.receipt && deviceType !== 'kiosk') {
                        console.log('[Windcave] Printing signature slip for customer to sign')
                        await this.printer.printTerminalReceipt(response.receipt, { title: 'SIGNATURE REQUIRED' })
                    }

                    return {
                        success: false,
                        signatureRequired: true,
                        transactionId: response.transactionId || transactionId,
                        displayLine1: response.displayLine1,
                        displayLine2: response.displayLine2,
                        signatureButtons: response.signatureButtons || {
                            button1Label: 'Accept',
                            button2Label: 'Decline'
                        },
                        receipt: response.receipt
                    }
                }

                if (response.signatureRequired && signatureAnswer) {
                    console.log('[Windcave] Still waiting for signature confirmation...')
                }

                await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
            } catch (error) {
                console.error('[Windcave] Status poll error:', error)
                await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
            }
        }

        this.status = PAYMENT_STATUS.FAILED

        this._saveLocalTerminalLog({
            txnType: 'PollResult',
            txnRef: transactionId,
            status: 'timeout',
            responseText: 'Payment timeout - please check terminal',
        })

        return {
            success: false,
            error: 'Payment timeout - please check terminal',
            status: 'timeout'
        }
    }

    async cancelPayment() {
        console.log('[Windcave] Cancel requested via HIT UI button press')

        if (this.statusCallback) {
            this.statusCallback('Cancelling transaction on terminal...')
        }

        try {
            const {useUserStore} = await import('../stores/user.js')
            const userStore = useUserStore()
            const businessId = userStore.activeBusinessId

            const {useDeviceRegistration} = await import('./useDeviceRegistration.js')
            const {getOrCreateDeviceUuid} = useDeviceRegistration()
            const deviceUuid = await getOrCreateDeviceUuid()

            const response = await useApi('/api/payment-terminals/cancel', {
                businessId: Number(businessId),
                transactionId: this.lastTransactionId || '',
                reason: 'Cancelled by POS operator',
                deviceUuid,
            }, {method: 'POST'}, 2)

            if (response.success) {
                this.status = PAYMENT_STATUS.CANCELLED
                if (this.statusCallback) {
                    this.statusCallback('Transaction cancelled')
                }
                return { success: true, message: 'Transaction cancelled' }
            }

            console.warn('[Windcave] Cancel response:', response)
            return {
                success: false,
                message: response.message || 'Cancel may not have succeeded - check terminal'
            }
        } catch (error) {
            console.error('[Windcave] Cancel error:', error)
            return {
                success: false,
                message: error.data?.message || error.message || 'Failed to cancel transaction'
            }
        }
    }

    async refundPayment(transactionId, amount, metadata = {}) {
        console.log('[Windcave] Processing refund via backend:', {transactionId, amount})

        try {
            const {useUserStore} = await import('../stores/user.js')
            const userStore = useUserStore()
            const businessId = userStore.activeBusinessId

            const {useDeviceRegistration} = await import('./useDeviceRegistration.js')
            const {getOrCreateDeviceUuid} = useDeviceRegistration()
            const deviceUuid = await getOrCreateDeviceUuid()

            const refundRequestBody = {
                transactionId,
                amount,
                reason: metadata.reason || 'POS refund',
                businessId: Number(businessId),
                deviceUuid
            }
            // Anchor on the original transaction + refund cents — re-issuing
            // a refund of the same amount on the same captured transaction
            // within 24h replays the cached response instead of double-
            // refunding through the gateway. A different refund amount on
            // the same transaction (e.g. partial refunds in two stages) gets
            // its own key.
            const refundCents = Math.round(Number(amount || 0) * 100)
            const refundIdemKey = `pt-refund-${transactionId}-${refundCents}`
            const response = await useApi(
                '/api/payment-terminals/refund',
                refundRequestBody,
                {method: 'POST', headers: {'Idempotency-Key': refundIdemKey}},
                2
            )

            this._saveLocalTerminalLog({
                txnType: 'Refund',
                txnRef: response.refundId || response.transactionId || transactionId,
                status: response.signatureRequired ? 'signature_required' : (response.success ? 'approved' : 'declined'),
                amount: amount,
                responseText: response.message,
                xmlRequest: response._xmlRequest || JSON.stringify(refundRequestBody),
                xmlResponse: response._xmlResponse,
                parsedResponse: response,
            })

            if (response.signatureRequired) {
                console.log('[Windcave] Refund requires signature verification')
                this.lastTransactionId = response.transactionId || response.refundId
                this.status = PAYMENT_STATUS.SIGNATURE_REQUIRED

                return {
                    success: false,
                    signatureRequired: true,
                    transactionId: response.transactionId || response.refundId,
                    refundId: response.refundId,
                    amount: response.amount,
                    displayLine1: response.displayLine1,
                    displayLine2: response.displayLine2,
                    signatureButtons: response.signatureButtons || {
                        button1Label: 'Accept',
                        button2Label: 'Decline'
                    },
                    receipt: response.receipt,
                    message: response.message
                }
            }

            return {
                success: response.success,
                refundId: response.refundId,
                transactionId: response.transactionId,
                amount: response.amount,
                timestamp: new Date().toISOString(),
                message: response.message,
                receipt: response.receipt
            }
        } catch (error) {
            console.error('[Windcave] Refund error:', error)
            // Pass through the error message from the backend
            const errorMessage = error.data?.message || error.message || 'Refund failed'
            return {
                success: false,
                message: errorMessage,
                error: errorMessage
            }
        }
    }

    async getLastMerchantReceipt(txnRef = null) {
        console.log('[Windcave] Requesting last merchant receipt:', { txnRef })

        try {
            const {useUserStore} = await import('../stores/user.js')
            const userStore = useUserStore()
            const businessId = userStore.activeBusinessId

            if (!businessId) {
                throw new Error('No business selected')
            }

            const {useDeviceRegistration} = await import('./useDeviceRegistration.js')
            const {getOrCreateDeviceUuid} = useDeviceRegistration()
            const deviceUuid = await getOrCreateDeviceUuid()

            const response = await useApi('/api/payment-terminals/last-receipt', {
                businessId: Number(businessId),
                txnRef: txnRef || undefined,
                receiptType: 3,
                deviceUuid,
            }, { method: 'POST' }, 2)

            if (response.success && response.receipt) {
                await this.printWindcaveReceipt(response.receipt)
                return {
                    success: true,
                    receipt: response.receipt,
                    txnRef: response.txnRef,
                    message: 'Merchant receipt printed',
                }
            }

            return {
                success: false,
                message: response.message || 'No receipt available',
            }
        } catch (error) {
            console.error('[Windcave] Last merchant receipt error:', error)
            return {
                success: false,
                message: error.data?.message || error.message || 'Failed to retrieve last merchant receipt',
            }
        }
    }

    async getStatus() {
        return {
            status: this.status,
            provider: 'windcave',
        }
    }

    async disconnect() {
        console.log('[Windcave] Disconnecting...')
        this.status = PAYMENT_STATUS.IDLE
        return {success: true}
    }

    setStatusCallback(callback) {
        this.statusCallback = callback
    }

    setSignatureCallback(callback) {
        this.signatureCallback = callback
    }
}

/**
 * Payment Provider Factory
 */
const providerInstances = new Map()

function getProviderInstance(providerType) {
    if (!providerInstances.has(providerType)) {
        let instance
        switch (providerType) {
            case PAYMENT_PROVIDERS.STRIPE_TERMINAL:
                instance = new StripeTerminalProvider()
                break
            case PAYMENT_PROVIDERS.TYRO:
                instance = new TyroTerminalProvider()
                break
            case PAYMENT_PROVIDERS.TYRO_KIOSK:
                instance = new TyroKioskProvider()
                break
            case PAYMENT_PROVIDERS.WINDCAVE:
                instance = new WindcaveProvider()
                break
            default:
                throw new Error(`Unknown payment provider: ${providerType}`)
        }
        providerInstances.set(providerType, instance)
    }
    return providerInstances.get(providerType)
}

export function clearProviderInstance(providerType) {
    if (providerType && providerInstances.has(providerType)) {
        const instance = providerInstances.get(providerType)
        if (instance?.disconnect) {
            instance.disconnect().catch(() => {})
        }
        providerInstances.delete(providerType)
        console.log('[PaymentProvider] Cleared provider instance:', providerType)
    }
}

// Disconnect every cached provider instance. Called on logout so that the
// next user's session doesn't inherit the previous user's pairing state,
// API keys, or terminal-side listener registrations. Without this, after
// logout + login as a different business the old Tyro/Stripe/Windcave
// instance was still in the module Map with the previous credentials, and
// the next charge could go through the wrong merchant if the new user
// never triggered a terminal config refresh.
export function clearAllProviderInstances() {
    for (const [providerType, instance] of providerInstances.entries()) {
        if (instance?.disconnect) {
            instance.disconnect().catch(() => {})
        }
        console.log('[PaymentProvider] Cleared provider instance:', providerType)
    }
    providerInstances.clear()
}

/**
 * Main composable
 */
export function usePaymentProvider() {
    const currentProvider = currentProviderRef
    const paymentStatus = paymentStatusRef
    const lastTransaction = lastTransactionRef
    const error = errorRef
    const statusMessage = statusMessageRef

    const setProvider = async (providerType, config = {}) => {
        try {
            activeProvider.value = providerType
            providerConfig.value = config

            currentProvider.value = getProviderInstance(providerType)

            if (currentProvider.value.setStatusCallback) {
                currentProvider.value.setStatusCallback((message) => {
                    statusMessage.value = message
                })
            }

            const result = await currentProvider.value.initialize(config)

            if (!result.success) {
                throw new Error('Failed to initialize payment provider')
            }

            return result
        } catch (err) {
            error.value = err.message
            throw err
        }
    }

    const syncPaymentStatus = async (paymentId) => {
        if (!paymentId) return null

        try {
            const response = await useApi(`/api/payment-terminals/payments/${paymentId}/status`, {}, {
                method: 'GET'
            }, 2)

            if (response.success) {
                return {
                    success: true,
                    status: response.status,
                    paymentStatus: response.paymentStatus,
                    transactionId: response.transactionId
                }
            }
            return null
        } catch (error) {
            console.error('[Payment] Failed to sync payment status:', error)
            return null
        }
    }

    const processCardPayment = async (amount, orderMetadata = {}, isOnline = true, onStatusUpdate = null) => {
        if (!currentProvider.value) {
            throw new Error('Payment provider not initialized')
        }

        if (!isOnline) {
            console.error('[Payment] Card payment attempted while offline - blocked')
            error.value = 'Card payments require an active internet connection'
            paymentStatus.value = PAYMENT_STATUS.FAILED
            return {
                success: false,
                error: 'Card payments require an active internet connection. Please use cash or wait until online.',
                errorCode: 'offline_card_blocked'
            }
        }

        error.value = null
        paymentStatus.value = PAYMENT_STATUS.PROCESSING

        try {
            const result = await currentProvider.value.processPayment(amount, orderMetadata)

            if (result.pending && result.transactionId && currentProvider.value.pollForStatus) {
                console.log('[Payment] Transaction pending, starting polling for:', result.transactionId)
                const deviceType = String(orderMetadata.orderId || '').startsWith('kiosk_') ? 'kiosk' : null
                const pollResult = await currentProvider.value.pollForStatus(result.transactionId, onStatusUpdate, null, deviceType)

                if (pollResult.success) {
                    lastTransaction.value = pollResult
                    paymentStatus.value = PAYMENT_STATUS.SUCCESS
                } else if (pollResult.signatureRequired) {
                    return pollResult
                } else {
                    error.value = pollResult.error
                    paymentStatus.value = PAYMENT_STATUS.FAILED
                }

                return pollResult
            }

            if (result.success) {
                lastTransaction.value = result
                paymentStatus.value = PAYMENT_STATUS.SUCCESS
            } else {
                error.value = result.error
                paymentStatus.value = PAYMENT_STATUS.FAILED
            }

            return result
        } catch (err) {
            error.value = err.message
            paymentStatus.value = PAYMENT_STATUS.FAILED
            throw err
        }
    }

    const processCashPayment = async (amount, received, orderMetadata = {}) => {
        error.value = null
        paymentStatus.value = PAYMENT_STATUS.PROCESSING

        await new Promise(resolve => setTimeout(resolve, 500))

        const change = received - amount

        if (change < 0) {
            error.value = 'Insufficient payment'
            paymentStatus.value = PAYMENT_STATUS.FAILED
            return {
                success: false,
                error: 'Insufficient payment received',
            }
        }

        const transaction = {
            success: true,
            transactionId: `cash_${Date.now()}`,
            amount,
            amountReceived: received,
            change,
            paymentMethod: 'cash',
            timestamp: new Date().toISOString(),
            metadata: orderMetadata,
        }

        if (orderMetadata.orderId && orderMetadata.businessId) {
            try {
                // Idempotency key scoped to (order, cents-amount). Stable across
                // a network-blip retry of THIS cash record. Caller is expected
                // to pass `idempotencyKey` in orderMetadata when they need a
                // distinct key (e.g. split payments doing $10 + $10 on the same
                // order); the bare scope works for the common one-shot case.
                const cents = Math.round(amount * 100)
                const idemKey =
                    orderMetadata.idempotencyKey ||
                    `pos-cash-${orderMetadata.businessId}-${orderMetadata.orderId}-${cents}`
                await useApi('/api/payment-terminals/record-cash', {
                    businessId: Number(orderMetadata.businessId),
                    orderId: Number(orderMetadata.orderId),
                    amount: amount,
                    amountReceived: received,
                    change: change,
                }, {method: 'POST', headers: {'Idempotency-Key': idemKey}}, 2)
                console.log('[Cash] Payment recorded to backend')
            } catch (recordError) {
                console.warn('[Cash] Failed to record payment to backend:', recordError)
            }
        }

        lastTransaction.value = transaction
        paymentStatus.value = PAYMENT_STATUS.SUCCESS

        return transaction
    }

    const processSplitPayment = async (payments, totalAmount, orderMetadata = {}, onPaymentProcessed = null, options = {}) => {
        error.value = null
        paymentStatus.value = PAYMENT_STATUS.PROCESSING

        const transactions = []
        let totalPaid = 0
        const { onConfirmPayment, onPaymentFailed, isOnline = true } = options

        let splitRecovery = null
        try {
            const { useSplitPaymentRecovery } = await import('./useSplitPaymentRecovery.js')
            splitRecovery = useSplitPaymentRecovery()
        } catch (e) {
            console.warn('[SplitPayment] Recovery composable not available:', e)
        }

        if (splitRecovery) {
            await splitRecovery.storeSplitPaymentState(
                orderMetadata.orderId,
                orderMetadata.localOrderId,
                totalAmount,
                payments,
                orderMetadata
            )
        }

        const cardPayments = payments.filter(p => p.method?.startsWith('card') && !p.processed)
        const cashPayments = payments.filter(p => p.method === 'cash' && !p.processed)
        const orderedPayments = [...cardPayments, ...cashPayments]

        const alreadyProcessedPayments = payments.filter(p => p.processed)
        for (const processed of alreadyProcessedPayments) {
            totalPaid += processed.amount
            transactions.push({
                success: true,
                amount: processed.amount,
                method: processed.method,
                alreadyProcessed: true,
                transactionId: processed.transactionId,
                last4: processed.last4,
                surchargeAmount: processed.surchargeAmount,
            })
            console.log('[SplitPayment] Skipping already processed payment:', processed.amount, processed.method)
        }

        if (orderedPayments.length === 0) {
            console.log('[SplitPayment] All payments already processed')
            const totalSurcharge = transactions
                .filter(t => t.surchargeAmount && Number(t.surchargeAmount) > 0)
                .reduce((sum, t) => sum + Number(t.surchargeAmount || 0), 0)

            if (splitRecovery) {
                await splitRecovery.clearSplitPaymentState()
            }

            lastTransaction.value = {
                success: true,
                transactionId: `split_${Date.now()}`,
                splitTransactions: transactions,
                completedTransactions: transactions,
                totalAmount,
                totalSurcharge,
                surchargeAmount: totalSurcharge,
                timestamp: new Date().toISOString(),
                metadata: orderMetadata,
            }
            paymentStatus.value = PAYMENT_STATUS.SUCCESS
            return lastTransaction.value
        }

        for (let i = 0; i < orderedPayments.length; i++) {
            const payment = orderedPayments[i]
            const originalIndex = payments.findIndex(p => p === payment)
            let result

            const remainingAfterThis = totalAmount - totalPaid - payment.amount
            const isLastPayment = i === orderedPayments.length - 1

            if (onConfirmPayment) {
                const shouldProceed = await onConfirmPayment({
                    paymentIndex: i,
                    originalIndex,
                    totalPayments: orderedPayments.length,
                    method: payment.method,
                    amount: payment.amount,
                    remainingAfter: Math.max(0, Math.round(remainingAfterThis * 100) / 100),
                    isLastPayment,
                    completedTransactions: transactions,
                    alreadyProcessedCount: alreadyProcessedPayments.length,
                })

                if (!shouldProceed) {
                    error.value = 'Payment cancelled by user'
                    paymentStatus.value = PAYMENT_STATUS.CANCELLED
                    return {
                        success: false,
                        error: error.value,
                        completedTransactions: transactions,
                        cancelledIndex: i,
                    }
                }
            }

            if (payment.method?.startsWith('card')) {
                if (i > 0 || alreadyProcessedPayments.length > 0) {
                    console.log('[SplitPayment] Waiting before next card payment...')
                    await new Promise(resolve => setTimeout(resolve, 1500))

                    if (activeProvider.value === PAYMENT_PROVIDERS.TYRO) {
                        const tyro = useTyro()
                        tyro.reset()
                        console.log('[SplitPayment] Tyro state reset for next payment')
                    }
                }

                result = await processCardPayment(payment.amount, {
                    ...orderMetadata,
                    splitPayment: true,
                    splitIndex: transactions.length + 1,
                    cardLabel: payment.method,
                }, isOnline)
            } else if (payment.method === 'cash') {
                result = await processCashPayment(payment.amount, payment.received || payment.amount, orderMetadata)
            }

            if (!result?.success) {
                error.value = `Payment ${transactions.length + 1} failed: ${result?.error || 'Unknown error'}`
                paymentStatus.value = PAYMENT_STATUS.FAILED

                if (onPaymentFailed) {
                    const retryDecision = await onPaymentFailed({
                        paymentIndex: i,
                        originalIndex,
                        method: payment.method,
                        amount: payment.amount,
                        error: result?.error || 'Unknown error',
                        completedTransactions: transactions,
                    })

                    if (retryDecision === 'retry') {
                        // Before re-charging, check whether the prior attempt
                        // actually settled at the gateway but failed locally
                        // (network blip during the confirmation roundtrip).
                        // Without this, retrying double-charges. Only relevant
                        // for Tyro card payments — Tyro exposes
                        // continueLastTransaction via useTyroRecovery; cash
                        // and Stripe-Terminal recovery paths live elsewhere.
                        const isTyroCard = payment.method?.startsWith('card')
                            && activeProvider.value === PAYMENT_PROVIDERS.TYRO
                        if (isTyroCard) {
                            try {
                                const { useTyroRecovery } = await import('./useTyroRecovery.js')
                                const tyroRecovery = useTyroRecovery()
                                const recovered = await tyroRecovery.recoverTransaction(providerConfig.value || {})
                                if (recovered?.success && recovered.status === 'approved') {
                                    console.warn('[SplitPayment] Recovered the failed Tyro leg via continueLastTransaction — consuming instead of re-charging')
                                    transactions.push({
                                        success: true,
                                        transactionId: recovered.transactionId,
                                        amount: recovered.amount ?? payment.amount,
                                        method: payment.method,
                                        originalIndex,
                                        last4: recovered.last4,
                                        cardType: recovered.cardType,
                                        surchargeAmount: recovered.surchargeAmount,
                                        customerReceipt: recovered.customerReceipt,
                                        merchantReceipt: recovered.merchantReceipt,
                                        recovered: true,
                                    })
                                    totalPaid += (recovered.amount ?? payment.amount)
                                    if (splitRecovery) {
                                        await splitRecovery.updatePaymentProcessed(originalIndex, transactions[transactions.length - 1])
                                    }
                                    if (onPaymentProcessed) {
                                        await onPaymentProcessed(originalIndex, transactions[transactions.length - 1])
                                    }
                                    continue
                                }
                            } catch (recErr) {
                                console.warn('[SplitPayment] Recovery probe failed; falling through to retry. Error:', recErr)
                            }
                        }
                        i--
                        continue
                    } else if (retryDecision === 'skip') {
                        continue
                    }
                }

                return {
                    success: false,
                    error: error.value,
                    completedTransactions: transactions,
                    failedIndex: i,
                }
            }

            transactions.push({...result, method: payment.method, originalIndex})
            totalPaid += payment.amount

            if (splitRecovery) {
                await splitRecovery.updatePaymentProcessed(originalIndex, result)
            }

            if (onPaymentProcessed) {
                await onPaymentProcessed(originalIndex, result)
            }
        }

        const totalPaidCents = Math.round(totalPaid * 100)
        const totalAmountCents = Math.round(totalAmount * 100)
        if (totalPaidCents !== totalAmountCents) {
            error.value = 'Split payment amounts do not match total'
            paymentStatus.value = PAYMENT_STATUS.FAILED
            return {
                success: false,
                error: error.value,
                completedTransactions: transactions,
            }
        }

        if (splitRecovery) {
            await splitRecovery.clearSplitPaymentState()
        }

        const totalSurcharge = transactions
            .filter(t => t.surchargeAmount && Number(t.surchargeAmount) > 0)
            .reduce((sum, t) => sum + Number(t.surchargeAmount || 0), 0)

        lastTransaction.value = {
            success: true,
            transactionId: `split_${Date.now()}`,
            splitTransactions: transactions,
            completedTransactions: transactions,
            totalAmount,
            totalSurcharge,
            surchargeAmount: totalSurcharge,
            timestamp: new Date().toISOString(),
            metadata: orderMetadata,
        }

        paymentStatus.value = PAYMENT_STATUS.SUCCESS

        return lastTransaction.value
    }

    const cancelPayment = async () => {
        if (!currentProvider.value) return

        try {
            const result = await currentProvider.value.cancelPayment()
            paymentStatus.value = PAYMENT_STATUS.CANCELLED
            return result
        } catch (err) {
            error.value = err.message
            throw err
        }
    }

    const refundPayment = async (transactionId, amount, metadata = {}) => {
        if (!currentProvider.value) {
            throw new Error('Payment provider not initialized')
        }

        try {
            return await currentProvider.value.refundPayment(transactionId, amount, metadata)
        } catch (err) {
            error.value = err.message
            throw err
        }
    }

    const resetPaymentState = () => {
        paymentStatus.value = PAYMENT_STATUS.IDLE
        lastTransaction.value = null
        error.value = null
        statusMessage.value = ''
    }

    const confirmSignature = async (accepted) => {
        if (!currentProvider.value) {
            throw new Error('Payment provider not initialized')
        }

        if (!currentProvider.value.confirmSignature) {
            throw new Error('Signature confirmation not supported by this provider')
        }

        try {
            const result = await currentProvider.value.confirmSignature(accepted)

            if (result.success && !result.pending) {
                paymentStatus.value = PAYMENT_STATUS.SUCCESS
                lastTransaction.value = result
            } else if (result.pending) {
                paymentStatus.value = PAYMENT_STATUS.PROCESSING
            } else {
                paymentStatus.value = PAYMENT_STATUS.FAILED
                error.value = result.error
            }

            return result
        } catch (err) {
            error.value = err.message
            paymentStatus.value = PAYMENT_STATUS.FAILED
            throw err
        }
    }

    const setQuestionCallback = (callback) => {
        if (currentProvider.value && currentProvider.value.setQuestionCallback) {
            currentProvider.value.setQuestionCallback(callback)
        }
    }

    const setReceiptCallback = (callback) => {
        if (currentProvider.value && currentProvider.value.setReceiptCallback) {
            currentProvider.value.setReceiptCallback(callback)
        }
    }

    return {
        // State
        activeProvider,
        paymentStatus,
        lastTransaction,
        error,
        statusMessage,
        PAYMENT_STATUS,
        PAYMENT_PROVIDERS,

        // Methods
        setProvider,
        processCardPayment,
        processCashPayment,
        processSplitPayment,
        cancelPayment,
        refundPayment,
        resetPaymentState,
        syncPaymentStatus,
        confirmSignature,
        setQuestionCallback,
        setReceiptCallback,
        setTransactionId: (txnId) => {
            if (currentProvider.value) {
                currentProvider.value.lastTransactionId = txnId
            }
        },
    }
}
